import { createFileRoute } from "@tanstack/react-router";
import { Button } from "@patient-watch/ui";
import { CheckCircle, Clock, Mail, MessageSquare, Target } from "lucide-react";
import { useLogoutMutation } from "../hooks/useLogoutMutation";
import { useCopy } from "../i18n/locale";

export const Route = createFileRoute("/instructions")({
    component: CompleteComponent,
});

function CompleteComponent() {
    const logoutMutation = useLogoutMutation();
    const copy = useCopy().instructions;
    return (
        <div className="flex flex-col mx-auto py-12 max-w-2xl">
            <div className="flex flex-col flex-1 justify-center items-center gap-y-8 px-4">
                <div className="space-y-4 text-center">
                    <h1 className="bg-clip-text bg-gradient-to-r from-green-600 to-emerald-600 font-semibold text-transparent text-3xl">
                        {copy.title}
                    </h1>
                    <p className="text-slate-600 text-lg">{copy.intro}</p>
                </div>

                {/* Study Purpose */}
                <div className="bg-green-50 p-8 border border-green-200 rounded-xl w-full">
                    <div className="flex items-start gap-4">
                        <div className="flex flex-shrink-0 justify-center items-center bg-green-100 mt-1 rounded-full w-10 h-10">
                            <Target className="w-5 h-5 text-green-600" />
                        </div>
                        <div>
                            <h3 className="mb-2 font-semibold text-green-800 text-lg">{copy.purposeTitle}</h3>
                            <p className="text-green-700 text-base">{copy.purposeBody}</p>
                        </div>
                    </div>
                </div>

                {/* Daily Process */}
                <div className="bg-slate-50 p-8 border border-slate-200 rounded-xl w-full">
                    <div className="space-y-8">
                        <div className="flex items-start gap-4">
                            <div className="flex flex-shrink-0 justify-center items-center bg-blue-100 mt-1 rounded-full w-10 h-10">
                                <MessageSquare className="w-5 h-5 text-blue-600" />
                            </div>
                            <div>
                                <h3 className="mb-2 font-semibold text-slate-800 text-lg">{copy.formsTitle}</h3>
                                <p className="text-slate-600 text-base">{copy.formsBody}</p>
                            </div>
                        </div>

                        <div className="flex items-start gap-4">
                            <div className="flex flex-shrink-0 justify-center items-center bg-purple-100 mt-1 rounded-full w-10 h-10">
                                <Clock className="w-5 h-5 text-purple-600" />
                            </div>
                            <div>
                                <h3 className="mb-3 font-semibold text-slate-800 text-lg">{copy.reportTitle}</h3>
                                <div className="space-y-1 text-slate-600 text-base">
                                    <p>• {copy.atSea}</p>
                                    <p>• {copy.ifYes}</p>
                                    <p>• {copy.glucose}</p>
                                    <p>• {copy.other}</p>
                                </div>
                            </div>
                        </div>

                        <div className="flex items-start gap-4">
                            <div className="flex flex-shrink-0 justify-center items-center bg-green-100 mt-1 rounded-full w-10 h-10">
                                <CheckCircle className="w-5 h-5 text-green-600" />
                            </div>
                            <div>
                                <h3 className="mb-2 font-semibold text-slate-800 text-lg">{copy.quickTitle}</h3>
                                <p className="text-slate-600 text-base">{copy.quickBody}</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Contact Information */}
                <div className="bg-blue-50 p-8 border border-blue-200 rounded-xl w-full">
                    <div className="flex items-start gap-4">
                        <div className="flex flex-shrink-0 justify-center items-center bg-blue-100 mt-1 rounded-full w-10 h-10">
                            <Mail className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                            <h3 className="mb-2 font-semibold text-blue-800 text-lg">{copy.helpTitle}</h3>
                            <p className="text-blue-700 text-base">{copy.helpBody}</p>
                            <p className="mt-2 text-blue-600 text-base">
                                📧{" "}
                                <a href="mailto:f.shojaee-moradie@surrey.ac.uk" className="underline">
                                    f.shojaee-moradie@surrey.ac.uk
                                </a>
                                <br />📞 {copy.direct} | {copy.mobile}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Study Duration */}
                <div className="w-full text-slate-600 text-sm text-center">
                    <p>
                        <strong>{copy.duration}</strong>
                    </p>
                    <p className="mt-1">
                        <strong>{copy.projectId}</strong>
                    </p>
                </div>

                <div className="pt-4 border-slate-200 border-t w-full">
                    <Button className="w-full" size="lg" onClick={() => logoutMutation.mutate()}>
                        {copy.logout}
                    </Button>
                </div>
            </div>
        </div>
    );
}
